/**
 * §7.2 Per-pathogen drilldown. Default loads SARS-CoV-2 JN.1; user
 * can switch via select. Pulls record from
 * @biomax/plugin-pathogen-database + paired genomic reference from
 * @biomax/plugin-pathogen-genomics.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findGenomicReference, hasBundledGenome } from '@biomax/plugin-pathogen-genomics';
import {
  CrossJurisdictionRouting,
  SAMPLE_ROUTING_TARGETS, SAMPLE_ROUTING_ORIGIN,
} from '../routing/CrossJurisdictionRouting';
import { queryCulturalRiskFactors } from '@biomax/plugin-region-database';
import { CulturalRiskSidebar } from '../cultural-risk/primitives';
import { pathogenToCulturalClass } from '../cultural-risk/classify';

export function PerPathogenDrilldown() {
  const all = listPathogens();
  const [id, setId] = useState<string>('sars-cov-2-jn1');

  const pathogen = useMemo(() => findPathogen(id) ?? all[0], [id, all]);
  const genome = useMemo(() => (pathogen ? findGenomicReference(pathogen.id) : undefined), [pathogen]);
  const tier2 = pathogen ? hasBundledGenome(pathogen.id) : false;

  if (!pathogen) return <div>Loading catalog…</div>;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.2</span>
        <h1 className="surface__title">{pathogen.names.common}</h1>
      </div>
      <div className="surface__sub">
        {pathogen.names.scientific}
        {pathogen.taxonomy.strain ? ` · ${pathogen.taxonomy.strain}` : ''}
        {' · '}{pathogen.taxonomy.family} ·
        {' '}{pathogen.transmission.primaryRoute}
      </div>

      <div style={{ display: 'flex', gap: 'var(--bm-space-3)' }}>
        <label className="simctl__field" style={{ flex: 1 }}>
          <span className="simctl__label">Pathogen</span>
          <select value={id} onChange={e => setId(e.target.value)}>
            {all.map(p => (
              <option key={p.id} value={p.id}>
                {p.names.common} ({p.id})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">R0 · naive</div>
          <div className="kpi__value">{pathogen.R0.value.toFixed(1)}</div>
          <div className="kpi__sub">{pathogen.R0.low.toFixed(1)}–{pathogen.R0.high.toFixed(1)} range</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Generation time</div>
          <div className="kpi__value">{pathogen.generationTimeDays.value.toFixed(1)}d</div>
          <div className="kpi__sub">{pathogen.generationTimeDays.low.toFixed(1)}–{pathogen.generationTimeDays.high.toFixed(1)}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Latent / infectious</div>
          <div className="kpi__value">
            {pathogen.latentPeriodDays.value.toFixed(1)} / {pathogen.infectiousPeriodDays.value.toFixed(1)}
          </div>
          <div className="kpi__sub">days</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">IFR · 65+</div>
          <div className="kpi__value">
            {(highestAgeBandIfr(pathogen) * 100).toFixed(2)}%
          </div>
          <div className="kpi__sub">oldest band</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Verification</div>
          <div className={`kpi__value ${pathogen.verification.status === 'verified' ? 'kpi__value--accent' : 'kpi__value--signal'}`}>
            {pathogen.verification.status}
          </div>
          <div className="kpi__sub">PMIDs round-tripped</div>
        </div>
      </div>

      {/* Phase 8.3 — Cross-jurisdiction routing layer + Phase 9.3 Cultural
          Risk sidebar. Side-by-side on wide viewports so the operator
          gets both views without scrolling; stack on narrow. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(280px, 1fr)',
        gap: 'var(--bm-space-3)', marginTop: 'var(--bm-space-3)',
      }}>
        <div>
          <div style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
            letterSpacing: '.6px', textTransform: 'uppercase',
            color: 'var(--bm-text-subtle)', marginBottom: 8,
          }}>Routing layer · suggested cross-jurisdiction notifications</div>
          <CrossJurisdictionRouting
            origin={SAMPLE_ROUTING_ORIGIN}
            targets={SAMPLE_ROUTING_TARGETS}
            surface="§7.2"
          />
        </div>
        <CulturalRiskSidebarFor pathogenId={pathogen.id} />
      </div>

      <div className="surface__grid" style={{ marginTop: 'var(--bm-space-4)' }}>
        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Age-stratified IFR</span>
            <span className="panel__meta">{pathogen.ifrByAgeBand.length} bands</span>
          </div>
          <table className="dtable">
            <thead>
              <tr><th>Age band</th><th className="num">IFR</th><th>Citation</th></tr>
            </thead>
            <tbody>
              {pathogen.ifrByAgeBand.map((b, i) => (
                <tr key={i}>
                  <td>{b.ageBandLow}–{b.ageBandHigh === 200 ? '∞' : b.ageBandHigh}</td>
                  <td className="num">{(b.ifr * 100).toFixed(4)}%</td>
                  <td style={{ fontSize: 11, color: 'var(--bm-text-muted)', fontFamily: 'var(--bm-font-mono)' }}>
                    PMID {b.citations[0]?.pmid ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Transmission</span>
            <span className="panel__meta">modes</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-2)' }}>
            {pathogen.transmission.modes.map(m => (
              <span key={m} style={{
                fontSize: 11,
                fontFamily: 'var(--bm-font-mono)',
                padding: '2px 8px',
                background: 'var(--bm-bg-subtle)',
                color: 'var(--bm-text-muted)',
                borderRadius: 'var(--bm-radius-sm)',
              }}>{m}</span>
            ))}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--bm-text)' }}>
            {pathogen.transmission.primaryRoute}
          </div>
          {pathogen.preSymptomaticTransmission && (
            <div style={{ marginTop: 16 }}>
              <div className="kpi__label">Pre-symptomatic share</div>
              <div className="kpi__value">{(pathogen.preSymptomaticTransmission.value * 100).toFixed(0)}%</div>
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Vaccines</span>
            <span className="panel__meta">{(pathogen.vaccines ?? []).length} entries</span>
          </div>
          {pathogen.vaccines && pathogen.vaccines.length > 0 ? (
            <table className="dtable">
              <thead>
                <tr><th>Name</th><th>Platform</th><th className="num">VE</th></tr>
              </thead>
              <tbody>
                {pathogen.vaccines.map((v, i) => (
                  <tr key={i}>
                    <td>{v.name}</td>
                    <td><span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>{v.platform}</span></td>
                    <td className="num">{(v.efficacy.value * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>No vaccine entries in catalog.</div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Therapeutics</span>
            <span className="panel__meta">{(pathogen.therapeutics ?? []).length} entries</span>
          </div>
          {pathogen.therapeutics && pathogen.therapeutics.length > 0 ? (
            <table className="dtable">
              <thead><tr><th>Name</th><th>Dosing</th></tr></thead>
              <tbody>
                {pathogen.therapeutics.map((t, i) => (
                  <tr key={i}>
                    <td>{t.name}</td>
                    <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{t.dosing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>No therapeutic entries in catalog.</div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Genomic reference</span>
            <span className="panel__meta">{tier2 ? 'Tier 2 bundled' : 'Tier 1 only'}</span>
          </div>
          {genome ? (
            <table className="dtable">
              <tbody>
                <tr><td>RefSeq</td><td className="num">{genome.refseq ?? '—'}</td></tr>
                <tr><td>NCBI Taxonomy</td><td className="num">{genome.ncbiTaxId ?? '—'}</td></tr>
                <tr><td>Genome length</td><td className="num">{genome.genomeLengthBp?.toLocaleString() ?? '—'} bp</td></tr>
                <tr><td>UniProt entries</td><td className="num">{(genome.uniprot ?? []).length}</td></tr>
                <tr><td>Tier 2 bundled</td><td className="num">{tier2 ? 'yes' : 'no'}</td></tr>
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>No genomic reference indexed.</div>
          )}
        </div>

        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Cross-immunity</span>
            <span className="panel__meta">{(pathogen.crossImmunity ?? []).length} edges</span>
          </div>
          {pathogen.crossImmunity && pathogen.crossImmunity.length > 0 ? (
            <table className="dtable">
              <thead><tr><th>From</th><th className="num">Reduction</th></tr></thead>
              <tbody>
                {pathogen.crossImmunity.map((c, i) => (
                  <tr key={i}>
                    <td>{c.otherPathogenId}</td>
                    <td className="num">{(c.reduction * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>No cross-immunity edges declared.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Phase 9.3 Cultural Risk sidebar wrapper — derives the cultural-risk
 *  pathogen class from the catalog pathogen id, queries the register
 *  for matching (any iso3) factors, and renders the sidebar. v1 queries
 *  cross-jurisdiction (no iso3 filter) so any matching pathogen-class
 *  factor surfaces; v2 should plumb the user's currently-selected
 *  region from ScenarioContext. */
function CulturalRiskSidebarFor({ pathogenId }: { pathogenId: string }) {
  const pathogenClass = pathogenToCulturalClass(pathogenId);
  const factors = useMemo(
    () => pathogenClass ? queryCulturalRiskFactors({ pathogenClass }) : [],
    [pathogenClass],
  );
  if (!pathogenClass) {
    return (
      <aside style={{
        padding: 14,
        background: 'var(--bm-bg-subtle)',
        border: '1px dashed var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        fontSize: 12, color: 'var(--bm-text-subtle)', lineHeight: 1.5,
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          textTransform: 'uppercase' as const, letterSpacing: '.5px', marginBottom: 6,
        }}>◑ Cultural / political context</div>
        No transmission-class mapping for this pathogen yet — Phase 9.3 covers
        the common families (filovirus / respiratory-droplet / faecal-oral /
        vector-borne / STI-BBV / zoonotic-spillover / healthcare-associated).
      </aside>
    );
  }
  return <CulturalRiskSidebar factors={factors} />;
}

function highestAgeBandIfr(pathogen: { ifrByAgeBand: { ageBandHigh: number; ifr: number }[] }): number {
  let highest = 0;
  let highestIfr = 0;
  for (const b of pathogen.ifrByAgeBand) {
    if (b.ageBandHigh > highest) { highest = b.ageBandHigh; highestIfr = b.ifr; }
  }
  return highestIfr;
}
